import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import React, { useEffect } from 'react';
import { clsx } from 'clsx';
import { ExpandCollapseIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/ExpandCollapseIcon/ExpandCollapseIcon';
import { CheckIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/CheckIcon/CheckIcon';
import { AddItemIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/AddItemIcon/AddItemIcon';
import { Separator } from 'src/components/ui/separator';
import { AddFAQItem } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/AddFAQAccordion/AddFAQItem';
import { CreateFAQPopup } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateFAQPopup/CreateFAQPopup';
import { useAddKnowledgeBaseDocumentMutation } from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import { AddItemIconDisabled } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/AddItemIcon/AddItemIconDisabled';
import type { KnowledgeBase } from 'src/services/knowledgeBaseService';
import type { KBFile } from 'src/utils/fileUtils';

export const AddFAQAccordion = ({
  items = [],
  add = () => {},
  isButtonsDisabled = false,
  knowledgeBase,
}: {
  items?: KBFile[];
  add?: (newFiles: KBFile[]) => void;
  isButtonsDisabled?: boolean;
  knowledgeBase?: Pick<KnowledgeBase, 'id'>;
}) => {
  const disabled = !items.length;
  const [value, setValue] = React.useState('');
  const loader = useLoader();
  const [addKnowledgeBaseDocument] = useAddKnowledgeBaseDocumentMutation();
  const [firstExpanded, setFirstExpanded] = React.useState(false);

  useEffect(() => {
    if (items.length && !firstExpanded && !value) {
      setFirstExpanded(true);
      setValue('new-card');
    }
  }, [items]);

  const addFAQ = async (data: { question: string; answer: string }) => {
    const newFiles: KBFile[] = [];

    loader.show();
    const response = await addKnowledgeBaseDocument({
      id: knowledgeBase.id,
      body: {
        name: data.question,
        text: data.answer,
        path: '/',
      },
    });

    loader.hide();
    // A failed request has no data; a document response with a status is an error too.
    if ('data' in response && !response.data.status) {
      newFiles.push(response.data);
    }

    newFiles.length && add(newFiles);
  };

  return (
    <div className={'w-full flex flex-row items-start gap-5'}>
      <div className={'pt-5'}>
        <CheckIcon className={clsx(!disabled ? 'visible' : 'invisible')} />
      </div>
      <Accordion
        value={value}
        onValueChange={setValue}
        type="single"
        className="w-full min-w-[455px] max-w-[455px]"
        collapsible={true}
        disabled={disabled}
      >
        <AccordionItem
          value="new-card"
          className={clsx(
            'group border border-[#B8C1C633] rounded-sm-5 bg-white transition-shadow shadow-none',
            !disabled && 'hover:shadow-[0_2px_2px_rgba(0,0,0,0.25)]',
          )}
        >
          <AccordionTrigger hideDefaultIcon className="py-2.5 px-5">
            <div className="flex items-center justify-between w-full">
              <div className={'flex flex-row gap-[7px]'}>
                <div className={'pt-[3px]'}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                  >
                    <path
                      d="M13.5 10.5V1.5L4.5 1.5V10.5H13.5ZM15 10.5C15 11.325 14.325 12 13.5 12H4.5C3.675 12 3 11.325 3 10.5L3 1.5C3 0.675 3.675 0 4.5 0L13.5 0C14.325 0 15 0.675 15 1.5V10.5ZM10.5 13.5V15H1.5C0.675 15 0 14.325 0 13.5L0 3.75H1.5L1.5 13.5H10.5ZM9.15 2.25C8.475 2.25 7.95 2.4 7.575 2.7C7.2 3 6.975 3.45 6.975 4.05H8.4C8.4 3.825 8.475 3.675 8.625 3.525C8.775 3.45 8.925 3.375 9.15 3.375C9.375 3.375 9.6 3.45 9.75 3.6C9.9 3.75 9.975 3.9 9.975 4.2C9.975 4.425 9.9 4.65 9.825 4.8C9.75 4.95 9.525 5.1 9.375 5.25C9 5.475 8.7 5.7 8.625 5.925C8.325 6.075 8.25 6.375 8.25 6.75L9.75 6.75C9.75 6.525 9.75 6.3 9.825 6.225C9.9 6.075 10.05 5.925 10.2 5.85C10.5 5.7 10.8 5.475 11.025 5.175C11.25 4.8 11.4 4.5 11.4 4.125C11.4 3.525 11.175 3.075 10.8 2.775C10.425 2.4 9.825 2.25 9.15 2.25ZM8.25 7.5V9H9.75V7.5L8.25 7.5Z"
                      fill="#05050D"
                    />
                  </svg>
                </div>
                <div className={'text-start'}>
                  <p className={'text-ebony text-sm font-semibold'}>
                    Add FAQs{' '}
                    <span
                      className={clsx(
                        'text-base-xs font-medium text-[#7A6BE5] bg-[#A498FF1A] px-[9px] py-[2px] rounded-[50px] ml-2.5',
                        disabled && 'invisible',
                      )}
                    >
                      {items.length}
                    </span>
                  </p>
                  <p className={'text-ebony_opacity_30 text-base-xs font-medium'}>
                    Compose custom Q&A pairs
                  </p>
                </div>
              </div>
              {!disabled && (
                <ExpandCollapseIcon
                  className={clsx(
                    'w-[12px] opacity-50 rotate-0 transition group-hover:opacity-100',
                    value && 'rotate-180',
                  )}
                />
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-0">
            {items.map((item, index, array) => {
              return (
                <div key={`item-${item.id}`}>
                  <AddFAQItem item={item} knowledgeBase={knowledgeBase} />
                  {index !== array.length - 1 && (
                    <Separator className={'h-[1px] bg-[#b8c1c657] -mx-5'} />
                  )}
                </div>
              );
            })}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <div className={clsx('pt-3.5 group hover:cursor-pointer')}>
        {isButtonsDisabled ? (
          <AddItemIconDisabled />
        ) : (
          <CreateFAQPopup
            trigger={<AddItemIcon />}
            addKnowledgeBaseSource={(data) => addFAQ(data)}
          />
        )}
      </div>
    </div>
  );
};
